import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import { Popover, PopoverTrigger } from '@neo/primitives/popover'
import { SelectWithGroups } from '@neo/primitives/select-with-groups'
import { Tooltip } from '@neo/primitives/tooltip'
import type { AxiosError } from 'axios'
import { Sparkles, SquareAsterisk } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import type { GenerateFunctionSchemaResponse } from 'v2/pages/flow/sidebar/types'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'
import z from 'zod'
import { NodevarList } from '../../../components/nodevar-list'
import type { AiActionNodeData } from '../../../types'
import { ActionConnectionLabel } from '../../../utils/action-connection-label'
import {
  decodeActionIdentifier,
  encodeActionIdentifier,
  initializeActionDataForForm,
} from '../../../utils/ai-action-utils'
import {
  buildActionOption,
  findActionConnectionForSavedProvider,
} from '../../../utils/integration-action-options'
import { SecretVariablesContent } from '../secret-variables-popover-v2'
import { DynamicActionInputs } from './action-inputs'

const ACTION_TYPE_OPTIONS = [
  { label: 'Execute app action', value: 'EXECUTE_ACTION' },
  { label: 'Use custom function', value: 'CUSTOM_CODE' },
]

interface ActionFormConfigProps {
  form: GenericFormType<AiActionNodeData['config']>
  nodeVars: string[]
  arrayIndex: number
  activeApps: RExternalOrgApp[] | undefined
  isLoadingApps: boolean
  generateFunctionSchema: (
    data: { code: string; function_name: string; function_description: string },
    options?: {
      onSuccess?: (data: GenerateFunctionSchemaResponse) => void
      onError?: (error: Error) => void
    },
  ) => void
  isGeneratingFunctionSchema: boolean
}

export const useActionFormConfig = ({
  form,
  nodeVars,
  arrayIndex,
  activeApps,
  isLoadingApps,
  generateFunctionSchema,
  isGeneratingFunctionSchema,
}: ActionFormConfigProps) => {
  const [isCodeGenModalOpen, setIsCodeGenModalOpen] = useState(false)
  // Secret popover state
  const [isSecretVarsOpen, setIsSecretVarsOpen] = useState(false)

  // Option values carry the app id too: different apps can share an action name.
  const actionOptions = useMemo(
    () =>
      (activeApps || []).map((app) => ({
        id: app.id,
        label: <ActionConnectionLabel app={app} />,
        options:
          app.actions?.map((appAction) =>
            buildActionOption(appAction, encodeActionIdentifier(appAction.action_name, app.id)),
          ) ?? [],
      })),
    [activeApps],
  )

  // Handle action selection by extracting the original action_name from the concatenated value
  // The concatenated value helps us uniquely identify which app the action belongs to
  const handleActionSelect = useCallback(
    (selectedActionId: string) => {
      // Helper function to clear all action form fields
      const clearActionFormFields = () => {
        form.setFieldValue(`actions[${arrayIndex}].action_inputs`, {})
        form.setFieldValue(`actions[${arrayIndex}].action_field_types`, {
          required_fields: {},
          optional_fields: {},
        })
      }

      // Helper function to set common action form data
      const setActionFormData = (
        app: RExternalOrgApp,
        actionName: string,
        actionInputs: Record<string, string>,
        actionFieldTypes: {
          required_fields: Record<string, 'auto' | 'manual'>
          optional_fields: Record<string, 'auto' | 'manual'>
        },
      ) => {
        form.setFieldValue(`actions[${arrayIndex}].org_app_uuid`, app.id)
        form.setFieldValue(`actions[${arrayIndex}].action_name`, actionName)
        form.setFieldValue(`actions[${arrayIndex}].app_provider`, app.provider)
        form.validateField(`actions[${arrayIndex}].action_name`, 'change')

        form.setFieldValue(`actions[${arrayIndex}].action_inputs`, actionInputs)
        form.setFieldValue(`actions[${arrayIndex}].action_field_types`, actionFieldTypes)
        form.validateField(`actions[${arrayIndex}].action_inputs`, 'change')
        form.validateField(`actions[${arrayIndex}].action_field_types`, 'change')

        form.setFieldMeta(`actions[${arrayIndex}].action_inputs`, (prev) => ({
          ...prev,
          isTouched: true,
        }))
        Object.keys(actionInputs).forEach((fieldName) => {
          const isOptionalField = actionFieldTypes.optional_fields[fieldName] !== undefined
          if (!isOptionalField) {
            form.setFieldMeta(`actions[${arrayIndex}].action_inputs.${fieldName}`, (prev) => ({
              ...prev,
              isTouched: true,
            }))
          }
        })
      }

      const decoded = decodeActionIdentifier(selectedActionId)
      if (!decoded) return
      const { actionName, appId } = decoded

      const appWithAction = (activeApps || []).find((app) => app.id === appId)
      if (!appWithAction) return
      const selectedAction = appWithAction.actions.find(
        (action) => action.action_name === actionName,
      )
      if (!selectedAction) return

      clearActionFormFields()

      const { actionInputs, actionFieldTypes } = initializeActionDataForForm({
        actionName: selectedAction.action_name,
        appId: appWithAction.id,
        activeApps: activeApps || [],
      })

      setActionFormData(appWithAction, selectedAction.action_name, actionInputs, actionFieldTypes)
    },
    [activeApps, form, arrayIndex],
  )

  const fields: GenericFieldConfig<AiActionNodeData['config']> = [
    {
      name: `actions[${arrayIndex}].action_type`,
      label: 'Action type',
      componentType: 'radioGroup',
      zodValidation: z.enum(['EXTRACT_VARIABLES', 'EXECUTE_ACTION', 'CUSTOM_CODE'], {
        message: 'Invalid action type',
      }),
      inputProps: {
        options: ACTION_TYPE_OPTIONS,
        'data-testid': 'select-action-type',
      },
    },
    {
      name: `actions[${arrayIndex}].action_name`,
      type: 'custom',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      component: ({ value }) => {
        // Get the current identifiers from the form
        const currentAppId = form.state.values.actions?.[arrayIndex]?.org_app_uuid
        const currentAppProvider = form.state.values.actions?.[arrayIndex]?.app_provider

        // For display purposes, reconstruct the value using org_app_uuid
        let displayValue: string | undefined

        if (typeof value === 'string' && currentAppId) {
          // New format: use org_app_uuid
          displayValue = encodeActionIdentifier(value, currentAppId)
        } else if (typeof value === 'string' && currentAppProvider && !currentAppId) {
          // Saved before org_app_uuid existed: bind through the provider, same as the agent picker.
          const appByProvider = findActionConnectionForSavedProvider(activeApps, currentAppProvider)
          if (appByProvider) {
            displayValue = encodeActionIdentifier(value, appByProvider.id)
            // Migrate the old format to new format
            form.setFieldValue(`actions[${arrayIndex}].org_app_uuid`, appByProvider.id)
          } else {
            // Fallback to the original value if we can't find the app
            displayValue = value as string
          }
        } else {
          displayValue = value as string
        }
        return (
          <SelectWithGroups
            groups={actionOptions}
            loading={isLoadingApps}
            placeholder="Select App "
            value={displayValue as string}
            onChange={(option) => {
              handleActionSelect(option || '')
            }}
            data-testid="select-action"
          />
        )
      },
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'EXECUTE_ACTION'),
      zodValidation: z.string({ invalid_type_error: 'Action name is required' }).min(1, {
        message: 'Action name is required',
      }),
    },
    {
      name: `actions[${arrayIndex}].action_instructions`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'EXECUTE_ACTION'),
      zodValidation: z.string({ invalid_type_error: 'Action instructions are required' }).min(1, {
        message: 'Action instructions are required',
      }),
      inputProps: {
        label: 'Action instructions',
        placeholder: 'Provide information and describe when this action should be executed.',
        className: 'min-h-[120px] bg-background',
        'data-testid': 'textarea-action-instructions',
        id: 'ai-action-instructions',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="#ai-action-instructions"
          />
        ),
      },
    },
    {
      name: `actions[${arrayIndex}].variable_name`,
      componentType: 'input',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) =>
        Boolean(values.actions?.[arrayIndex]?.action_type === 'EXTRACT_VARIABLES'),
      zodValidation: z.string({ invalid_type_error: 'Variable name is required' }).min(1, {
        message: 'Variable name is required',
      }),
      eventDataExtractor: (e) =>
        z
          .string()
          .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
          .parse(e.target?.value),
      inputProps: {
        placeholder: 'Enter variable name to extract',
      },
    },
    {
      name: `actions[${arrayIndex}].variable_instructions`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) =>
        Boolean(values.actions?.[arrayIndex]?.action_type === 'EXTRACT_VARIABLES'),
      zodValidation: z.string({ invalid_type_error: 'Variable instructions are required' }).min(1, {
        message: 'Variable instructions are required',
      }),
      inputProps: {
        label: 'Variable instructions',
        placeholder: 'Variable description (e.g. extract the users email from the conversation)',
        className: 'min-h-[120px] bg-background',
        'data-testid': 'textarea-variable-instructions',
      },
    },
    {
      name: `actions[${arrayIndex}].action_inputs`,
      type: 'custom',
      onChangeListenTo: [
        `actions[${arrayIndex}].action_name`,
        `actions[${arrayIndex}].action_type`,
      ],
      component: ({ form: componentForm }) => (
        <DynamicActionInputs
          form={componentForm}
          nodeVars={nodeVars}
          activeApps={activeApps}
          arrayIndex={arrayIndex}
        />
      ),
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'EXECUTE_ACTION'),
      zodValidation: z.record(z.string(), z.string().nullable()).nullish(),
      eventDataExtractor: (data) => data,
    },
    {
      name: `actions[${arrayIndex}].code_name`,
      type: 'sub-item',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      componentType: 'input',
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'CUSTOM_CODE'),
      zodValidation: z.string({ invalid_type_error: 'Function name is required' }).min(1, {
        message: 'Function name is required',
      }),
      eventDataExtractor: (e) =>
        z
          .string()
          .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
          .parse(e.target?.value),
      inputProps: {
        placeholder: 'Enter function name',
        'data-testid': 'input-function-name',
      },
    },
    {
      name: `actions[${arrayIndex}].code_description`,
      type: 'sub-item',
      componentType: 'textarea',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'CUSTOM_CODE'),
      zodValidation: z.string({ invalid_type_error: 'Function description is required' }).min(1, {
        message: 'Function description is required',
      }),
      inputProps: {
        label: 'Function Description',
        placeholder: 'Describe what this function does and when to use it.',
        className: 'min-h-[120px] bg-background',
        id: 'code-function-description',
        endIcon: (
          <NodevarList nodevars={nodeVars} inputElementSelector="#code-function-description" />
        ),
        'data-testid': 'textarea-function-description',
      },
    },
    {
      name: `actions[${arrayIndex}].code_body`,
      type: 'custom',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'CUSTOM_CODE'),
      eventDataExtractor: (data) => data,
      component: ({ onChange, value }) => (
        <div className="-mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-s-600 text-secondary">
              Code <span className="text-s-400 italic text-tertiary">(Python3)</span>
            </span>
            <div className="flex items-center gap-1">
              <ClipboardCopyButton
                textToCopy={value as string}
                tooltipText="Copy code"
                className="size-4 text-secondary"
                data-testid="button-copy-code"
              />
              <Popover open={isSecretVarsOpen} onOpenChange={setIsSecretVarsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-s-600 text-secondary p-1 hover:bg-accent rounded-sm data-[state=open]:bg-primary data-[state=open]:text-primary-foreground"
                    data-testid="button-open-secret-vars"
                    aria-label="View secret variables"
                  >
                    <Tooltip content="Add secret variables">
                      <SquareAsterisk className="w-4 h-4" />
                    </Tooltip>
                  </button>
                </PopoverTrigger>
                <SecretVariablesContent
                  code={value as string}
                  onInsert={(code) => form.setFieldValue(`actions[${arrayIndex}].code_body`, code)}
                  onClose={() => setIsSecretVarsOpen(false)}
                />
              </Popover>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => setIsCodeGenModalOpen(true)}
                data-testid="button-open-code-gen"
                aria-label="Generate code"
                className="form-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate code
              </Button>
            </div>
          </div>
          <CodeEditor
            language="python"
            value={value as string}
            onChange={onChange}
            className="border border-st2 rounded-md overflow-auto"
            data-testid="code-editor"
          />

          {/* Generate Schema Button - moved here from slot field */}
          <div className="mt-2">
            <ButtonWithLoader
              loading={isGeneratingFunctionSchema}
              variant="outline"
              type="button"
              size="sm"
              onClick={() => {
                const currentValue = form.state.values.actions?.[0]
                if (currentValue) {
                  generateFunctionSchema(
                    {
                      code: currentValue.code_body || '',
                      function_name: currentValue.code_name || '',
                      function_description: currentValue.code_description || '',
                    },
                    {
                      onSuccess: (data) => {
                        form.setFieldValue(
                          `actions[${arrayIndex}].code_function_schema`,
                          data.function_schema,
                        )
                        form.validateField(`actions[${arrayIndex}].code_function_schema`, 'change')
                      },
                      onError: (error) => {
                        errorToast({
                          title: 'Failed to generate function schema',
                          description: getErrorText(error as AxiosError),
                        })
                      },
                    },
                  )
                }
              }}
            >
              Generate Function Schema
            </ButtonWithLoader>
          </div>
        </div>
      ),
      zodValidation: z.string({ invalid_type_error: 'Code body is required' }).min(1, {
        message: 'Code body is required',
      }),
    },
    {
      name: `actions[${arrayIndex}].code_function_schema`,
      type: 'custom',
      onChangeListenTo: [`actions[${arrayIndex}].action_type`],
      visible: (values) => Boolean(values.actions?.[arrayIndex]?.action_type === 'CUSTOM_CODE'),
      component: ({ onChange, value }) => {
        if (!value) return null
        const jsonValue = JSON.stringify(value, null, 2)

        return (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-s-600 text-secondary">
                Function Schema <span className="text-s-400 italic text-tertiary">(JSON)</span>
              </span>
              <ClipboardCopyButton
                textToCopy={jsonValue}
                tooltipText="Copy function schema"
                className="mr-1 size-4 text-secondary"
                data-testid="button-copy-schema"
              />
            </div>
            <CodeEditor
              language="json"
              value={JSON.stringify(value, null, 2)}
              className="border border-st2 rounded-md overflow-auto bg-background"
              data-testid="code-editor-schema"
              onChange={(code) => {
                try {
                  onChange(JSON.parse(code))
                } catch (_e) {
                  onChange(code)
                }
              }}
            />
          </div>
        )
      },
      eventDataExtractor: (data) => data,
      zodValidation: z
        .any()
        .refine((value) => value && typeof value === 'object' && Object.keys(value).length > 0, {
          message: 'Function schema is required. Click the button to generate.',
        }),
    },
  ]

  return {
    fields,
    isCodeGenModalOpen,
    setIsCodeGenModalOpen,
  }
}
